import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { reduxForm, Field } from 'redux-form';
import { Form, Header, Modal, Message, Button } from 'semantic-ui-react';
// Actions
import {
    requestLoadReEntryWarning,
    setReEntryWarning,
    cancelLoadReEntryWarning,
    resetReEntryWarning
} from 'services/reEntryWarning/actions';
// Global Selectors
import { selectReEntryWarning } from 'services/reEntryWarning/selectors';
// Types
import { AppState } from 'services/types';
// Components
// @ts-ignore
import UpdateGroupValueOnNewGroupCreation from 'components/UpdateGroupValueOnNewGroupCreation';
// @ts-ignore
import CreateGroupButton from 'components/CreateGroupButton';
import GroupModal from 'components/GroupModal';
// @ts-ignore
import SchemaName from '../../../../components/SchemaName';
// @ts-ignore
import SchemaHasInstructionsGate from '../../../../components/SchemaHasInstructionsGate';
// @ts-ignore
import GroupDropdownFilterDropdown from '../../../../components/GroupDropdownFilterDropdown';
// @ts-ignore
import GroupOptions from '../../../../components/GroupOptions';
// @ts-ignore
import UsersTagInput from '../../../../components/UsersTagInput';
// @ts-ignore
import DefaultFieldValue from '../../../../components/DefaultFieldValue';
// @ts-ignore
import SchemaEntityTypeahead from '../SchemaEntityTypeahead';
// @ts-ignore
import SchemaGroupIdIsEnabledGate from '../SchemaGroupIdIsEnabledGate';
// @ts-ignore
import SchemaAttributesList from './components/SchemaAttributesList';
import ReEntryPeriodDaysWarning from './components/ReEntryPeriodDaysWarning';
// Selectors
// @ts-ignore
import { selectCurrentSeasonIsComplete } from '../../../../services/currentSeasonId/selectors';
// @ts-ignore
import { makeSelectEventValue } from '../../../../services/events/selectors';
// @ts-ignore
import { selectInitialValues, makeSelectFormValue } from './selectors';
// Form
import {
    normalizers,
    validators,
    RenderDatePicker,
    RenderDropdown
    // @ts-ignore
} from '../../../../forms';
// Constants
// @ts-ignore
import { FORM_NAME } from './constants';
import GeneralEventSubmitButton from './components/GeneralEventSubmitButton';

// Validators
const { isRequired, isNotBeforeTwoYearsAgo } = validators;

// Normalizers
const { toNull } = normalizers;

// Guardrail: normalize and dedupe semicolon-separated description strings
const normalizeDescription = (value?: string): string | undefined => {
    if (!value) return undefined;
    const tokens = value
        .split(';')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    const deduped = Array.from(new Set(tokens));
    const joined = deduped.join('; ');
    return joined || undefined;
};

interface OwnProps {
    eventId?: number;
    schemaId?: number;
    error?: string;
    handleSubmit?: Function;
    onClose: Function;
    disableIntructions?: boolean;
    onSubmit: Function;
}

interface ConnectedProps {
    initialValues: ReturnType<typeof selectInitialValues>;
    eventSchemaId?: number;
    entityId?: number;
    startDate?: string;
    endDate?: string;
    disabled: ReturnType<typeof selectCurrentSeasonIsComplete>;
    isReEntryWarning: ReturnType<typeof selectReEntryWarning>;
}

interface DispatchProps {
    requestLoadReEntryWarning: typeof requestLoadReEntryWarning;
    cancelLoadReEntryWarning: typeof cancelLoadReEntryWarning;
    setReEntryWarning: typeof setReEntryWarning;
    resetReEntryWarning: typeof resetReEntryWarning;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

class GeneralJobForm extends React.PureComponent<Props> {
    componentDidUpdate(prevProps: Props) {
        const {
            entityId,
            startDate,
            endDate,
            isReEntryWarning,
            setReEntryWarning: setWarning,
            requestLoadReEntryWarning: requestLoadWarning
        } = this.props;

        const isNewPayload =
            entityId !== prevProps.entityId ||
            startDate !== prevProps.startDate ||
            endDate !== prevProps.endDate;

        if (isNewPayload) {
            if (isReEntryWarning) setWarning(false);
            if (entityId && (startDate || endDate)) {
                const startDt = startDate || (endDate as string);
                const endDt = endDate || (startDate as string);

                requestLoadWarning(entityId, startDt, endDt);
            }
        }
    }

    componentWillUnmount(): void {
        const {
            cancelLoadReEntryWarning: cancelLoadWarning,
            resetReEntryWarning: resetWarning
        } = this.props;

        cancelLoadWarning();
        resetWarning();
    }

    get schemaId(): any {
        const { eventSchemaId, schemaId } = this.props;
        return eventSchemaId || schemaId;
    }

    handleSubmit = (values: any): void => {
        const { onSubmit } = this.props;

        onSubmit({
            ...values,
            jobs: values.jobs.map((job: any) => ({
                ...job,
                assignedUsers: job.assignedUsers || [],
                documentSetAttributes:
                    job.documentSetAttributes &&
                    Object.values(job.documentSetAttributes).map(
                        (attr: any) => ({
                            ...attr,
                            description: normalizeDescription(attr.description)
                        })
                    )
            }))
        });
    };

    render(): React.ReactElement {
        const {
            startDate,
            endDate,
            disabled,
            error,
            disableIntructions,
            handleSubmit,
            onClose
        } = this.props;

        return (
            <React.Fragment>
                <Modal.Header>
                    <SchemaName schemaId={this.schemaId} />
                    {' Details'}
                </Modal.Header>
                <Modal.Content className="constrain-width">
                    <Form
                        onSubmit={handleSubmit!(this.handleSubmit)}
                        autoComplete="off"
                    >
                        <SchemaEntityTypeahead
                            name="entityId"
                            validate={isRequired}
                            schemaId={this.schemaId}
                            disabled={disableIntructions || disabled}
                        />
                        <SchemaGroupIdIsEnabledGate schemaId={this.schemaId}>
                            <UpdateGroupValueOnNewGroupCreation
                                formName={FORM_NAME}
                                name="groupId"
                            />
                            <GroupOptions>
                                {(options: any): React.ReactElement => (
                                    <Field
                                        component={RenderDropdown}
                                        name="groupId"
                                        label="Group (optional)"
                                        placeholder="Add group"
                                        normalize={toNull}
                                        disabled={disabled}
                                        options={options}
                                        clearable={!disabled}
                                        fluid
                                        search
                                        selection
                                        selectOnBlur={false}
                                        renderOptions={
                                            !disabled && (
                                                <GroupDropdownFilterDropdown
                                                    resetOnNewGroupCreation
                                                />
                                            )
                                        }
                                    />
                                )}
                            </GroupOptions>
                            {!disabled && <CreateGroupButton />}
                            {!disabled && <GroupModal />}
                        </SchemaGroupIdIsEnabledGate>
                        <DefaultFieldValue
                            formName={FORM_NAME}
                            name="jobs[0].jobType"
                            value={null}
                        />
                        <UsersTagInput
                            label="Staff member (optional)"
                            placeholder="Add staff member"
                            name="jobs[0].assignedUsers"
                            disabled={disabled}
                        />
                        <Field
                            name="startDate"
                            label="Start date (optional)"
                            placeholder="Add date"
                            component={RenderDatePicker as any}
                            validate={isNotBeforeTwoYearsAgo}
                            dateFormat="EEE do MMM"
                            normalize={toNull}
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            maxDate={endDate}
                            disabled={disabled}
                        />
                        <Field
                            name="endDate"
                            label="End date (optional)"
                            placeholder="Add date"
                            component={RenderDatePicker as any}
                            validate={isNotBeforeTwoYearsAgo}
                            dateFormat="EEE do MMM"
                            normalize={toNull}
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate}
                            disabled={disabled}
                        />
                        <SchemaHasInstructionsGate schemaId={this.schemaId}>
                            <Header as="h3">Instructions</Header>
                            <SchemaAttributesList
                                schemaId={this.schemaId}
                                disableIntructions={disableIntructions}
                            />
                        </SchemaHasInstructionsGate>
                        {error && <Message negative>{error}</Message>}
                        <ReEntryPeriodDaysWarning />
                        {/* Custom submission section due to custom event submit button <SubmissionSection /> */}
                        <div className="submission-section">
                            <div className="buttons">
                                <Button
                                    type="button"
                                    onClick={(): void => {
                                        onClose();
                                    }}
                                    basic
                                    primary
                                >
                                    Cancel
                                </Button>
                                <GeneralEventSubmitButton
                                    disabled={disabled}
                                    label="Save"
                                />
                            </div>
                        </div>
                    </Form>
                </Modal.Content>
            </React.Fragment>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    initialValues: selectInitialValues,
    eventSchemaId: makeSelectEventValue('schemaId'),
    entityId: makeSelectFormValue('entityId'),
    startDate: makeSelectFormValue('startDate'),
    endDate: makeSelectFormValue('endDate'),
    disabled: selectCurrentSeasonIsComplete,
    isReEntryWarning: selectReEntryWarning
});

const mapDispatchToProps = {
    requestLoadReEntryWarning,
    setReEntryWarning,
    cancelLoadReEntryWarning,
    resetReEntryWarning
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(
    reduxForm<any, any>({
        form: FORM_NAME
    })(GeneralJobForm)
);
